import React from 'react'
import { Card, CardContent } from '../ui'
import { useI18n } from '../../i18n'
import { featureFlags } from '../../config/featureFlags'
import {
  fetchApplicabilitySummary,
  type ApplicabilityPayload,
  type TopologyControl,
} from '../../services/complianceTopologyApi'

interface Props {
  systemId: string
  standardCode: string | null
}

function ControlGroup({
  title,
  controls,
  badgeClass,
  badgeLabel,
}: {
  title: string
  controls: TopologyControl[]
  badgeClass: string
  badgeLabel: string
}) {
  if (!controls.length) return null
  return (
    <div className="mt-3">
      <h4 className="text-xs font-semibold text-slate-700 mb-1">{title}</h4>
      <ul className="space-y-1">
        {controls.map((c) => (
          <li
            key={`${c.standardCode}-${c.controlCode}`}
            className="text-xs flex flex-wrap items-center gap-1 border border-slate-100 rounded px-2 py-1"
            title={(c.explanations || []).map((e) => e.message).join(' ')}
          >
            <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${badgeClass}`}>
              {badgeLabel}
            </span>
            <span className="font-mono">{c.controlCode}</span>
            {c.controlTitle ? <span className="text-slate-500">— {c.controlTitle}</span> : null}
          </li>
        ))}
      </ul>
    </div>
  )
}

export default function InformationSystemApplicabilityPanel({ systemId, standardCode }: Props) {
  const { t } = useI18n()
  const [data, setData] = React.useState<ApplicabilityPayload | null>(null)
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (!featureFlags.COMPLIANCE_TOPOLOGY_V1 || !standardCode) {
      setData(null)
      return
    }
    setLoading(true)
    setError(null)
    void fetchApplicabilitySummary(systemId, standardCode)
      .then((d) => setData(d))
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load applicability'))
      .finally(() => setLoading(false))
  }, [systemId, standardCode])

  if (!featureFlags.COMPLIANCE_TOPOLOGY_V1) return null
  if (!standardCode) return null

  return (
    <section id="passport-section-applicability" className="scroll-mt-4 mt-4">
      <Card>
        <CardContent className="p-4">
          <h3 className="text-sm font-semibold text-slate-800">
            {t('complianceTopology.applicability', 'Applicability')}
          </h3>
          <p className="text-xs text-slate-500 mt-1">
            {t('complianceTopology.appliesBecause', 'Applies because')} — {standardCode}
          </p>
          {loading ? <p className="text-xs text-slate-400 mt-2">…</p> : null}
          {error ? <p className="text-xs text-red-600 mt-2">{error}</p> : null}
          {data?.metrics ? (
            <div className="flex flex-wrap gap-3 mt-2 text-xs text-slate-600">
              {data.metrics.evidenceCoveragePercent != null ? (
                <span>
                  {t('complianceTopology.evidenceCoverage', 'Evidence coverage')}:{' '}
                  {data.metrics.evidenceCoveragePercent}%
                </span>
              ) : null}
              {data.metrics.vulnerabilityPressure != null ? (
                <span>
                  {t('complianceTopology.operationalPressure', 'Operational pressure')}:{' '}
                  {data.metrics.vulnerabilityPressure}
                </span>
              ) : null}
            </div>
          ) : null}
          <ControlGroup
            title={t('complianceTopology.organizationWide', 'Organization-wide controls')}
            controls={data?.organizationControls || []}
            badgeClass="bg-indigo-50 text-indigo-700"
            badgeLabel={t('complianceTopology.organizationWide', 'Organization-wide')}
          />
          <ControlGroup
            title={t('complianceTopology.inheritedControls', 'Inherited controls')}
            controls={data?.inheritedControls || []}
            badgeClass="bg-indigo-50 text-indigo-700"
            badgeLabel={t('complianceTopology.inheritedControl', 'Inherited')}
          />
          <ControlGroup
            title={t('complianceTopology.scopedControls', 'Scoped controls')}
            controls={data?.scopedControls || []}
            badgeClass="bg-emerald-50 text-emerald-700"
            badgeLabel={t('complianceTopology.scopedControl', 'Scoped')}
          />
          {!loading && !error && !(data?.applicableControls?.length) ? (
            <p className="text-xs text-slate-400 mt-2">
              {t('complianceTopology.scopedSummaryUnavailable', 'Scoped summary unavailable')}
            </p>
          ) : null}
        </CardContent>
      </Card>
    </section>
  )
}
